import { Shield } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authClient } from '@/lib/auth'

export function TwoFactorVerify() {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const next = [...code]
    next[index] = value.slice(-1)
    setCode(next)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const totp = code.join('')
    if (totp.length !== 6) return

    setError('')
    setLoading(true)

    try {
      const result = await authClient.twoFactor.verifyTotp({ code: totp })
      if (result.error) {
        setError(result.error.message ?? 'Invalid code')
        setCode(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
        setLoading(false)
        return
      }
      navigate('/', { replace: true })
    } catch {
      setError('Verification failed')
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-full border border-dashed border-border bg-surface p-3">
            <Shield className="h-6 w-6 text-accent" strokeWidth={1.5} />
          </div>
          <h1 className="font-mono text-xs font-medium tracking-widest text-ink uppercase">
            Two-Factor Authentication
          </h1>
          <p className="mt-2 font-mono text-[11px] text-ink-faint">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-dashed border-border bg-surface p-6"
        >
          {error && (
            <div className="mb-4 rounded-md border border-dashed border-negative/30 bg-negative/5 px-3 py-2 font-mono text-xs text-negative">
              {error}
            </div>
          )}

          <div className="flex justify-center gap-2">
            {['d0', 'd1', 'd2', 'd3', 'd4', 'd5'].map((key, i) => (
              <input
                key={key}
                ref={(el) => {
                  inputRefs.current[i] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-12 w-10 rounded-md border border-dashed border-border bg-canvas text-center font-mono text-lg text-ink"
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading || code.join('').length !== 6}
            className="mt-5 w-full rounded-md bg-accent py-2.5 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  )
}
